/**
 * Client side of `GET /api/waadoo-platform/projects/<p>/odoo-versions`
 * (plugins/odoo-backend/src/waadooPlatform/odooVersionsRoute.ts): the
 * Odoo version each environment of a project states — « Odoo 19.0 », built
 * on 26/09/2026 — behind `OdooVersionBadge`.
 *
 * Asked by the backend and not by the browser: Odoo's `version_info` answers
 * on the environment's own host, which sends no CORS header for this app.
 *
 * `OdooVersionsResponse` is re-declared rather than imported, for the reason
 * given in `pipelines.ts`: it is the contract of the route, to be changed on
 * both sides at once.
 *
 * KEYED BY NAMESPACE, not by branch: the namespace is what both sides read
 * from the same object (the ArgoCD Application's destination here, the
 * labelled Namespace there), while the backend's branch is rebuilt from the
 * namespace's name.
 *
 * ONE REQUEST PER PROJECT for every card of a page, through the
 * `sharedLoad.ts` pattern, the cache hanging off the app's `FetchApi` as in
 * `grafanaOrgs.ts`.
 *
 * NEVER IN THE WAY. Loading, a refusal, a failure, an environment Odoo did
 * not answer for: no version, and no badge — nothing is reserved for it.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import {
  useOnValueChange,
  createSharedLoad,
  type SharedLoad,
  type SharedLoadOptions,
} from '@internal/plugin-waadoo-core';

/** One environment of the route's answer. */
export interface EnvironmentOdooVersion {
  namespace: string;
  branch?: string;
  series?: string;
  /** `YYYY-MM-DD`. */
  build?: string;
  raw?: string;
  error?: 'unreachable' | 'unreadable';
}

/** The route's answer. */
export interface OdooVersionsResponse {
  environments: EnvironmentOdooVersion[];
}

/** What a badge shows. */
export interface OdooVersion {
  /** « 19.0 », « saas~18.4 ». */
  series: string;
  /** `YYYY-MM-DD`, when the version carries a build date. */
  build?: string;
  /** The version as Odoo states it, « 19.0-20260926 ». */
  raw: string;
}

/** The version of each namespace that stated one. */
export type OdooVersions = Readonly<Record<string, OdooVersion>>;

/**
 * How long one answer is reused by the screens. The backend keeps a version
 * 10 minutes; this only spares a request when moving between pages.
 */
export const ODOO_VERSIONS_TTL_MS = 60_000;

/** One identity for "no version", so that a memo over the map does not churn. */
const NO_VERSIONS: OdooVersions = Object.freeze({});

const loads = new WeakMap<FetchApi, Map<string, SharedLoad<OdooVersions>>>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const optionalString = (value: unknown) =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * The versions of the payload, by namespace; an entry without series is
 * dropped. Held in an object without prototype, as in `grafanaOrgs.ts`: a
 * namespace named `constructor` must look up as undefined.
 */
export function readOdooVersions(payload: unknown): OdooVersions {
  const versions: Record<string, OdooVersion> = Object.create(null);
  if (!isRecord(payload) || !Array.isArray(payload.environments)) {
    return versions;
  }
  for (const item of payload.environments) {
    if (!isRecord(item)) {
      continue;
    }
    const namespace = optionalString(item.namespace);
    const series = optionalString(item.series);
    if (!namespace || !series) {
      continue;
    }
    const build = optionalString(item.build);
    versions[namespace] = {
      series,
      raw: optionalString(item.raw) ?? series,
      ...(build && /^\d{4}-\d{2}-\d{2}$/.test(build) ? { build } : {}),
    };
  }
  return versions;
}

interface RequestOptions {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  /** The project — its `waadoo.ovh/project` label. */
  project: string;
}

/** One request, unshared. Rejects on a failed request. */
export async function fetchOdooVersions(
  options: RequestOptions,
): Promise<OdooVersions> {
  // Absolute backend URL: in development the frontend runs on another port.
  const baseUrl = await options.discoveryApi.getBaseUrl('waadoo-platform');
  const response = await options.fetchApi.fetch(
    `${baseUrl}/projects/${encodeURIComponent(options.project)}/odoo-versions`,
  );
  if (!response.ok) {
    throw new Error(`Odoo versions API: HTTP ${response.status}`);
  }
  return readOdooVersions(await response.json());
}

/** The shared read of `project`'s versions, made on first use. Never pruned. */
function sharedVersions(
  fetchApi: FetchApi,
  project: string,
): SharedLoad<OdooVersions> {
  let byProject = loads.get(fetchApi);
  if (!byProject) {
    byProject = new Map();
    loads.set(fetchApi, byProject);
  }
  let shared = byProject.get(project);
  if (!shared) {
    shared = createSharedLoad<OdooVersions>(ODOO_VERSIONS_TTL_MS);
    byProject.set(project, shared);
  }
  return shared;
}

/**
 * The shared read behind {@link useOdooVersions}. Rejects when the request
 * fails, and the failure is not kept: the next card to mount asks again.
 * `force`: a Refresh, past the shared answer.
 */
export function loadOdooVersions(
  options: RequestOptions,
  loadOptions?: SharedLoadOptions,
): Promise<OdooVersions> {
  return sharedVersions(options.fetchApi, options.project).load(
    () => fetchOdooVersions(options),
    loadOptions,
  );
}

/**
 * The Odoo versions of `project`'s environments, by namespace. Empty while
 * loading, on a failed first read, and without a project.
 *
 * `refreshKey`: a new value — a page's Refresh — reads them again past the
 * shared answer, the badges kept on screen meanwhile and on a failure. Every
 * instance for the project takes any answer fetched for it
 * (`SharedLoad.subscribe`): the header of the project page follows the
 * environments card's Refresh.
 */
export function useOdooVersions(
  project: string | undefined,
  refreshKey?: unknown,
): OdooVersions {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  // Kept with the project it answers for, as in `useGrafanaOrgIds`: a
  // caller may pass another project without being remounted.
  const [loaded, setLoaded] = useState<{
    project: string;
    versions: OdooVersions;
  }>();

  useEffect(() => {
    if (!project) {
      return undefined;
    }
    let active = true;
    const load = async () => {
      try {
        const versions = await loadOdooVersions({
          fetchApi,
          discoveryApi,
          project,
        });
        if (active) setLoaded({ project, versions });
      } catch {
        // See the header: no badge.
        if (active) setLoaded(undefined);
      }
    };
    // `load` turns its own failure into state, so nothing can reject.
    void load();
    return () => {
      active = false;
    };
  }, [project, fetchApi, discoveryApi]);

  // Whoever fetched them: this hook's Refresh, another instance's.
  useEffect(() => {
    if (!project) {
      return undefined;
    }
    return sharedVersions(fetchApi, project).subscribe(versions =>
      setLoaded({ project, versions }),
    );
  }, [project, fetchApi]);

  // The answer reaches every instance through the subscription above; a
  // failure keeps the badges shown.
  useOnValueChange(refreshKey, () => {
    if (project) {
      loadOdooVersions(
        { fetchApi, discoveryApi, project },
        { force: true },
      ).catch(() => undefined);
    }
  });

  return loaded && loaded.project === project ? loaded.versions : NO_VERSIONS;
}
